// OWNER: documents module (FR-2.3). Shared components other modules render on record detail views:
//   <LinkedDocuments entityType entityId canEdit? title? headingLevel? />  — documents linked to a record, with
//       "Upload a document" (upload-and-link) and "Link an existing document" (DocumentPicker), plus unlink.
//   <DocumentPicker onSelect multiple? categories? label? disabled? />   — choose vault documents or upload new.
// API: GET/POST/DELETE /api/w/:id/documents/links?entityType=&entityId=. Props are stable — other modules use them.
import { lazy, Suspense, useMemo, useState } from 'react';
import { Download, Link2, Paperclip, Search, Unlink, Upload } from 'lucide-react';
import {
  DEFAULT_CATEGORY_FOR_ENTITY,
  DOCUMENT_CATEGORY_LABELS,
  ENTITY_TYPE_LABELS,
  type DocumentCategory,
  type DocumentDetail,
  type DocumentSummary,
  type EntityType,
} from '@seventide/types';
import { useCan } from '../../hooks/useCan';
import { useOptionalWorkspace } from '../../hooks/useWorkspace';
import { errorMessage } from '../../lib/api';
import { plural } from '../../lib/format';
import { cn } from '../../lib/cn';
import { getSignedUrl, useDocuments, useLinkDocuments, useLinkedDocuments, useUnlinkDocument } from '../../features/documents/api';
import { CategoryBadge, DocumentThumb } from '../../features/documents/components/bits';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { ConfirmDialog, Dialog } from '../ui/Dialog';
import { Field } from '../ui/Field';
import { Input, Select } from '../ui/Input';
import { Link } from '../ui/Link';
import { Spinner } from '../ui/Spinner';
import { Tabs } from '../ui/Tabs';
import { toast } from '../ui/toastStore';
import { DateText } from './Basics';

// The upload form (with photo crop) is only downloaded when someone opens it.
const UploadForm = lazy(() => import('../../features/documents/components/UploadForm').then((m) => ({ default: m.UploadForm })));

async function download(wsId: string, id: string) {
  try {
    const r = await getSignedUrl(wsId, id, 'attachment');
    const a = document.createElement('a');
    a.href = r.url;
    a.download = r.filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    toast({ title: "We couldn't download the document", description: errorMessage(e), tone: 'danger' });
  }
}

export interface LinkedDocumentsProps {
  entityType: EntityType;
  entityId: string;
  title?: string;
  headingLevel?: 2 | 3;
  /** Allow linking/uploading (pass `useCan('documents','edit')`). */
  canEdit?: boolean;
}

/** Documents linked to a record (evidence, receipts, letters). */
export function LinkedDocuments({ entityType, entityId, title = 'Linked documents', headingLevel = 2, canEdit }: LinkedDocumentsProps) {
  const ws = useOptionalWorkspace();
  const canView = useCan('documents', 'view');
  const canEditDocs = useCan('documents', 'edit');
  const editable = (canEdit ?? true) && canEditDocs;
  const q = useLinkedDocuments(entityType, entityId, canView);
  const link = useLinkDocuments();
  const unlink = useUnlinkDocument();
  const [uploadOpen, setUploadOpen] = useState(false);
  const H = `h${headingLevel}` as 'h2' | 'h3';
  const headingId = `ld-${entityType}-${entityId}`;
  const wsId = ws?.wsId ?? '';
  const recordLabel = ENTITY_TYPE_LABELS[entityType].toLowerCase();
  const items = q.data?.items ?? [];

  return (
    <section
      aria-labelledby={headingId}
      data-entity-type={entityType}
      data-entity-id={entityId}
      className="rounded-[var(--radius-card)] border border-border bg-surface p-4 shadow-card"
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <H id={headingId} className="flex items-center gap-2 text-lg">
          <Paperclip aria-hidden="true" className="h-5 w-5 text-primary" />
          {title}
          {items.length > 0 && <span className="font-normal text-muted">({items.length})</span>}
        </H>
        {canView && editable && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" leftIcon={<Upload aria-hidden="true" className="h-4 w-4" />} onClick={() => setUploadOpen(true)}>
              Upload a document
            </Button>
            <DocumentPicker
              label="Link an existing document"
              multiple
              excludeIds={items.map((d) => d.id)}
              onSelect={(ids) => {
                if (ids.length === 0) return;
                link.mutate(
                  { entityType, entityId, documentIds: ids },
                  {
                    onSuccess: (r) => toast({ title: r.linked === 1 ? 'Document linked' : `${r.linked} documents linked`, tone: 'success' }),
                    onError: (e) => toast({ title: "We couldn't link the document", description: errorMessage(e), tone: 'danger' }),
                  },
                );
              }}
            />
          </div>
        )}
      </div>

      {!canView ? (
        <p className="text-muted">Documents haven't been shared with you in this workspace.</p>
      ) : q.isPending ? (
        <Spinner label="Loading linked documents" />
      ) : q.isError ? (
        <Alert tone="danger" action={<Button size="sm" variant="secondary" onClick={() => void q.refetch()}>Try again</Button>}>
          {errorMessage(q.error, "We couldn't load the linked documents.")}
        </Alert>
      ) : items.length === 0 ? (
        <p className="text-muted">
          No documents linked to this {recordLabel} yet.
          {editable && ' Upload a receipt, letter or statement, or link one that is already in the vault.'}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {items.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-3 py-2">
              <span className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-border" aria-hidden="true">
                <DocumentThumb wsId={wsId} id={d.id} hasThumbnail={d.hasThumbnail} kind={d.fileKind} className="[&_svg]:h-6 [&_svg]:w-6" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <Link to={`/w/${wsId}/records/documents/${d.id}`} className="break-words">
                  {d.title}
                </Link>
                <span className="text-sm text-muted">
                  {DOCUMENT_CATEGORY_LABELS[d.category]} · <DateText iso={d.documentDate ?? d.createdAt} />
                </span>
              </span>
              <span className="flex gap-1">
                <Button size="sm" variant="ghost" leftIcon={<Download aria-hidden="true" className="h-4 w-4" />} onClick={() => void download(wsId, d.id)}>
                  Download{' '}<span className="sr-only">{d.title}</span>
                </Button>
                {editable && (
                  <ConfirmDialog
                    trigger={
                      <Button size="sm" variant="ghost" leftIcon={<Unlink aria-hidden="true" className="h-4 w-4" />}>
                        Unlink{' '}<span className="sr-only">{d.title}</span>
                      </Button>
                    }
                    title="Unlink this document?"
                    description={`"${d.title}" stays in the documents vault. It just won't be shown with this ${recordLabel} any more.`}
                    confirmLabel="Unlink"
                    onConfirm={async () => {
                      await unlink.mutateAsync({ entityType, entityId, documentId: d.id });
                      toast({ title: 'Document unlinked', tone: 'success' });
                    }}
                  />
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {editable && (
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen} title={`Upload a document for this ${recordLabel}`} size="lg">
          <Suspense fallback={<Spinner label="Opening the upload form" />}>
            {uploadOpen && (
              <UploadForm
                defaultCategory={DEFAULT_CATEGORY_FOR_ENTITY[entityType]}
                link={{ entityType, entityId }}
                onCancel={() => setUploadOpen(false)}
                onDone={() => setUploadOpen(false)}
              />
            )}
          </Suspense>
        </Dialog>
      )}
    </section>
  );
}

export interface DocumentPickerProps {
  /** Called with the chosen document ids. */
  onSelect: (documentIds: string[]) => void;
  multiple?: boolean;
  /** Restrict to categories (FR-2.2). */
  categories?: string[];
  /** Button text (default "Attach a document"). */
  label?: string;
  disabled?: boolean;
  /** Documents not to offer (e.g. already linked). */
  excludeIds?: string[];
  /** Category pre-selected when uploading a new document. */
  defaultCategory?: DocumentCategory;
}

/** Choose existing vault documents or upload a new one, e.g. to attach a receipt to a transaction. */
export function DocumentPicker({
  onSelect,
  multiple = false,
  categories,
  label = 'Attach a document',
  disabled,
  excludeIds,
  defaultCategory,
}: DocumentPickerProps) {
  const canView = useCan('documents', 'view');
  const canEdit = useCan('documents', 'edit');
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'vault' | 'upload'>('vault');
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const allowed = categories?.length ? (categories as DocumentCategory[]) : undefined;
  const list = useDocuments(
    { q: q || undefined, category: category ? [category as DocumentCategory] : allowed, limit: 50, offset: 0 },
    { enabled: open && canView },
  );
  const exclude = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);
  const items = (list.data?.items ?? []).filter((d) => !exclude.has(d.id));

  const close = (v: boolean) => {
    setOpen(v);
    if (!v) {
      setSelected([]);
      setDraft('');
      setQ('');
      setCategory('');
      setTab('vault');
    }
  };
  const toggle = (id: string) =>
    setSelected((s) => (multiple ? (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]) : s[0] === id ? [] : [id]));
  const choose = () => {
    onSelect(selected);
    close(false);
  };

  if (!canView) return null;

  const vault = (
    <div className="flex flex-col gap-3">
      <form
        role="search"
        className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]"
        onSubmit={(e) => {
          e.preventDefault();
          setQ(draft.trim());
        }}
      >
        <Field label="Search the vault" hint="Titles, tags and text inside documents">
          <Input
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => setQ(draft.trim())}
            suffix={
              <Button type="submit" size="sm" aria-label="Search">
                <Search aria-hidden="true" className="h-5 w-5" />
              </Button>
            }
          />
        </Field>
        <Field label="Category">
          <Select
            value={category}
            placeholder={allowed ? 'Any of the suggested categories' : 'All categories'}
            options={(allowed ?? (Object.keys(DOCUMENT_CATEGORY_LABELS) as DocumentCategory[])).map((c) => ({
              value: c,
              label: DOCUMENT_CATEGORY_LABELS[c],
            }))}
            onChange={(e) => setCategory(e.target.value)}
          />
        </Field>
      </form>
      {list.isPending && <Spinner label="Loading documents" />}
      {list.isError && <Alert tone="danger">{errorMessage(list.error)}</Alert>}
      {list.data && items.length === 0 && (
        <p className="text-muted">
          {q || category ? 'No documents match. Try other words, or upload a new document.' : 'There are no documents to choose from yet. Upload one instead.'}
        </p>
      )}
      {items.length > 0 && (
        <fieldset className="flex flex-col gap-2">
          <legend className="sr-only">{multiple ? 'Choose documents' : 'Choose a document'}</legend>
          {items.map((d: DocumentSummary) => {
            const checked = selected.includes(d.id);
            const id = `pick-${d.id}`;
            return (
              <div
                key={d.id}
                className={cn('flex items-start gap-3 rounded-lg border-2 p-3', checked ? 'border-primary bg-primary-soft' : 'border-border')}
              >
                <input
                  id={id}
                  type={multiple ? 'checkbox' : 'radio'}
                  name="document-picker"
                  checked={checked}
                  onChange={() => toggle(d.id)}
                  className="mt-1 h-6 w-6 shrink-0 cursor-pointer accent-[var(--st-primary)]"
                />
                <label htmlFor={id} className="flex min-w-0 flex-1 cursor-pointer flex-col gap-1">
                  <span className="font-bold break-words">{d.title}</span>
                  <span className="flex flex-wrap items-center gap-2 text-sm text-muted">
                    <CategoryBadge category={d.category} />
                    <DateText iso={d.documentDate ?? d.createdAt} />
                  </span>
                  {d.match?.snippet && <span className="text-sm">{d.match.snippet}</span>}
                </label>
              </div>
            );
          })}
        </fieldset>
      )}
    </div>
  );

  const upload = (
    <Suspense fallback={<Spinner label="Opening the upload form" />}>
      {open && tab === 'upload' && (
        <UploadForm
          multiple={multiple}
          defaultCategory={defaultCategory ?? allowed?.[0]}
          categories={allowed}
          submitLabel={multiple ? 'Upload and choose' : 'Upload and choose'}
          onDone={(docs: DocumentDetail[]) => {
            onSelect(docs.map((d) => d.id));
            close(false);
          }}
        />
      )}
    </Suspense>
  );

  return (
    <>
      <Button
        size="sm"
        variant="secondary"
        disabled={disabled}
        leftIcon={<Link2 aria-hidden="true" className="h-4 w-4" />}
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      <Dialog
        open={open}
        onOpenChange={close}
        title={multiple ? 'Choose documents' : 'Choose a document'}
        size="lg"
        footer={
          tab === 'vault' ? (
            <>
              <Button variant="secondary" onClick={() => close(false)}>
                Cancel
              </Button>
              <Button onClick={choose} disabled={selected.length === 0}>
                {selected.length > 1 ? `Use ${plural(selected.length, 'document')}` : 'Use this document'}
              </Button>
            </>
          ) : undefined
        }
      >
        {canEdit ? (
          <Tabs
            label="Where the document comes from"
            value={tab}
            onValueChange={(v) => setTab(v as 'vault' | 'upload')}
            items={[
              { value: 'vault', label: 'From the vault', content: vault },
              { value: 'upload', label: 'Upload a new one', content: upload },
            ]}
          />
        ) : (
          vault
        )}
      </Dialog>
    </>
  );
}
